/* global window */
import queryString from 'query-string';
import { myLocationProfileQuery } from './stores/profile/location/location-graphql-query';
import { profileQuery } from './stores/profile/company/profile-graphql-query';
import * as contractInfoQueries from './stores/contracting/contract-activities-products/graphql-queries';
import { myProductMutation } from './stores/profile/product/product-graphql-query';
// import * as companyGQL from './stores/company/company/company-graphql-query';

const API_ROOT = '/proxy/';
const API_CONTENTFUL = 'https://cdn.contentful.com/';
const API_URL = process.env.REAL_API_URL || process.env.API_URL;

const jsonDecode = async response => {
  try {
    const jsonRet = await response.json();
    return jsonRet;
  } catch (jsonErr) {
    return {};
  }
}

export const callApi = async (endpoint, method = 'get', bodyParam = null, useOriginalUrl) => {
  const fullUrl = (endpoint.indexOf(API_ROOT) === -1) && !useOriginalUrl ? API_ROOT + endpoint : endpoint;
  const body = bodyParam ? JSON.stringify(bodyParam) : null;
  let response;
  try  {
    response = await fetch(fullUrl, {
      method,
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
      body,
    });
  } catch (err) {
    response = err;
  }
  const json = await jsonDecode(response);
  if (!response.ok) {
    return {
      error: {
        response,
        json,
        request: {
          url: fullUrl,
          body,
          method,
        },
      },
    };
  }
  return { response: json };
}

export const callApiJWT = async (endpoint, method = 'get', bodyParam = null) => {
  const fullUrl = (/^https?:\/\/*/.test(endpoint)) ? endpoint : `${API_URL}${endpoint}`;
  const token = localStorage.getItem('jwt');
  if (!token) return {};
  const body = bodyParam ? JSON.stringify(bodyParam) : null;
  let response;
  try  {
    response = await fetch(fullUrl, {
      method,
      credentials: 'same-origin',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body,
    });
  } catch (err) {
    response = err;
  }
  const json = await jsonDecode(response);
  if (!response.ok) {
    // if something is wrong with jwt, force reauthenticate
    if (json.code === 'InvalidCredentials') {
      if (json.message.indexOf('jwt expired') > -1) {
        localStorage.removeItem('jwt');
      }
      window.location.assign(`${window.location.protocol}//${window.location.host}/signin?logout=true`);
    }
    return {
      error: {
        response,
        json,
        request: {
          url: fullUrl,
          body,
          method,
        },
      },
    };
  }
  const newToken = response.headers.get('new-token');
  if (Boolean(newToken)) localStorage.setItem('jwt', newToken);
  return { response: json };
};

export const callApiwithToken = async (endpoint, method = 'get', bodyParams = null) => {
  const fullUrl = (/^https?:\/\/*/.test(endpoint)) ? endpoint : API_URL + endpoint;

  const body = bodyParams ? JSON.stringify(bodyParams) : null;
  const hashtoken = sessionStorage.getItem('hashtoken') || '';
  const encryptedemail = sessionStorage.getItem('encryptedemail') || '';
  let response;
  try  {
    response = await fetch(fullUrl, {
      method,
      // credentials: 'same-origin',
      // mode: 'no-cors',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        hashtoken,
        encryptedemail,
      },
      body,
    });
  } catch (err) {
    response = err;
  }
  const json = await jsonDecode(response);
  if (!response.ok) {
    return {
      error: {
        response,
        json,
        request: {
          url: fullUrl,
          body,
          method,
        },
      },
    };
  }
  return { response: json };
}

// api services
export const account = {
  getUsersById: ({ companyId }) => callApi(`/proxy/company/${companyId}/users`, 'get'),
  confirmEmail: token => callApi('/proxy/confirm-signin-email', 'put', { token }),
  login: payload => callApi(`/signin`, 'post', payload, true),
  shellSignUp: payload => callApiwithToken('/contractingSignUp/signUp', 'post', payload),
  loginAsUser: payload => callApi('/loginAsUser', 'post', payload, true),
  sendInternalEmail: payload => callApi('/internal/notification', 'post', payload),
  sendNotification: payload => callApiJWT('/sendNotification', 'post', payload),
  updateUIpreferences: payload => callApiJWT('/updateUIpreferences', 'put', payload),
};

export const partner = {
  getCompanyInfo: ({ partnerId }) =>
    callApi(`partner/${partnerId}`, 'get'),
  getActivities: ({ assetId, activityType }) => callApiJWT(`/asset-activities/${assetId}?activityType=${activityType || ''}`, 'get'),
  addConnectionAsPartner: ({ connectionId, targetCompanyId }) =>
    callApiJWT(`/partner-management/partner/connection`, 'post', { connectionId, targetCompanyId }),
};

export const workspace = {
  getConnections: (selectedConnection = '') => callApi(`workspace/connections${selectedConnection ? `/${selectedConnection}` : ''}`, 'get'),
  archiveConnection: connection => callApi(`workspace/connections/${connection}/archive`, 'get'),
  deleteMessage: message => callApi(`workspace/message/${message}`, 'delete'),
  getChannels: (connection, channel = '', contract = '') => {
    if (!contract) return callApi(`workspace/connections/${connection}/channels`, 'get');
    return callApi(`workspace/connections/${connection}/channels/${channel}/${contract}`, 'get');
  },
  getConversations: (assetId, channelId) => {
    return callApi(`workspace/connections/${assetId}/channels/${channelId}/conversation`, 'get');
  },
  archiveConversation: (connection, channel, contract) => callApi(`workspace/connections/${connection}/channels/${channel}/archive/${contract || ''}`, 'post'),
  newMessage: (body) => callApi(`workspace/newMessage`, 'post', body),
  saveSelectedChannel: (partnerId, channel) => callApi(`workspace/connections/${partnerId}`, 'post', { lastChannel: channel }),
  conversationsRead: ({ assetId, assetType = 'partner', messageId }) =>
    callApiJWT(`/workspace/markRead`, 'post', { assetId, assetType, messageId }),
  statusMessages: () => callApiJWT(`/statusMessages`, 'get'),
  unreadAcceptedRead: (connectionIds) => callApi('/statusMessages/markConnectionAcceptedAsRead', 'post', { connectionIds }),
  getActivities: partner.getActivities,
};

export const historyFeed = {
  getHistory: (contractId) => callApi(`/proxy/contracting/getHistoryFeed/${contractId}/contract`, 'get'),
};

export const fileUrls = {
  getDownloadUrlSync: (path) => {
    if (path && path.indexOf('/') > -1) return `${API_ROOT}s3/file?key=${encodeURIComponent(path)}`;
    return `/filebyhash/${path}`;
  },
  getDownloadTCContractingDoc: ({ docId, collection = 'contract', locale, ratesOnly, downloadRateFormat, forAL }) => {
    let url;
    if (forAL) {
      url = (downloadRateFormat || '').split('_')[0] === 'quickview'
        ? '/landingproxy/contracting/pipeXls' : `/landingproxy/download-pdf`;
    } else {
      url = (downloadRateFormat || '').split('_')[0] === 'quickview'
        ? '/proxy/contracting/pipeXls' : `/proxy/download-pdf`;
    }
    const qs = queryString.stringify({
      docId,
      locale,
      collection,
      ratesOnly: downloadRateFormat === 'condensed' || ratesOnly,
      type: (downloadRateFormat || '').indexOf('quickview') > -1
        ? (downloadRateFormat || '').split('_')[1] : '',
    });
    return (`${url}?${qs}`);
  },
  downloadGroup: ({
    collection = 'contract',
    locale,
    downloadRateFormat,
    forAL,
    docIds,
  }) => {
    const qs = queryString.stringify({
      locale,
      collection,
      downloadRateFormat,
    });
    const url = `/contracting/download-group?${qs}`;
    const params = { docIds };
    const callFunc = forAL ? callApiwithToken : callApiJWT;
    return callFunc(url, 'post', params);
  },
  checkJobUrl: ({ jobUrl, forAL }) => {
    const callFunc = forAL ? callApiwithToken : callApiJWT;
    return callFunc(jobUrl, 'get');
  },
};

export const downloads = {
  downloadTCContractingDoc: payload => {
    // window.open(fileUrls.getDownloadTCContractingDoc(payload));
    const downloadUrl = fileUrls.getDownloadTCContractingDoc(payload);
    const a = document.createElement('a');
    a.download = true;
    a.target = '_blank';
    a.setAttribute('download', true);
    a.href = downloadUrl;
    a.dispatchEvent(new MouseEvent('click'));
  },
  getXls: (docType, docId, exportType) => callApi(`contracting/exportXls/${docType}/${docId}?type=${exportType}`, 'get'),
};

export const contracting = {
  deleteOrUndelete: (params) => callApi('/proxy/contracting/deleteOrUndelete', 'put', params), // params: { contractIds, state: <true or false> } true means delete, false means restore
  getContractList: params => callApiJWT('/contracting/contracts', 'post', params),
  checkContractReadiness: draftId => callApiJWT((`/contracting/checkContractReadiness/${draftId}`), 'put'),
  getRespondedContractList: ({ requestId }) => callApiJWT(`/contracting/request/${requestId}/contracts`, 'get'),
  getContractRequestInfo: ({ requestId }) => {
    return callApiJWT(`/contracting/getContractInfo/${requestId}`, 'post', {
      query: `
          query getContract ($contractId: ID!, $viewerId: ID!, $viewBy: String!) {
            Contract (id: $contractId, viewerId: $viewerId, viewBy: $viewBy) {
              contractId
              startDate
              endDate
              supplierId
              buyerId
              buyerChecked
              buyerInfo {
                submissionCriteria
                termsAndConditions
              }
              partners {
                id
                name
                avatar
                tags {
                  id
                  name
                }
                contacts {
                  firstName
                  lastName
                  email
                  id
                }
              }
              contacts {
                id
                firstName
                lastName
                email
              }
              from {
                avatar
                name
                email
              }
              to {
                avatar
                name
                partnerId
                contactIds
              }
            }
          }
        `,
      contractId: requestId,
      collection: 'contract',
    });
  },
  getContractPartners: ({ contractId }) => {
    return callApiJWT(`/contracting/getContractInfo/${contractId}`, 'post', {
      query: `
          query getContract ($contractId: ID!, $viewerId: ID!, $viewBy: String!) {
            Contract (id: $contractId, viewerId: $viewerId, viewBy: $viewBy) {
              partners {
                id
                name
                tags {
                  id
                  name
                }
                contacts {
                  id
                  firstName
                  lastName
                  email
                }
              }
            }
          }
        `,
      contractId,
      collection: 'contract',
    });
  },
  getContractInfo: ({ collection, docId, type, forAL }) => {
    // type: 'metaData', 'content'
    const callFunc = forAL ? callApi : callApiJWT;
    return callFunc(`${forAL ? '/landingproxy' : ''}/contracting/getContractInfo/${docId}`, 'post', {
      query: (() => {
        if (type === 'policyNotes') {
          return collection === 'contract' || collection === 'workspace' ? contractInfoQueries.getContractDetailsQuery : contractInfoQueries.draftTemplateContractDetails;
        }
        return collection === 'contract' || collection === 'workspace' ? contractInfoQueries.getLeftNavQuery : contractInfoQueries.draftTemplateLeftNav;
      })(),
      contractId: docId,
      collection: collection === 'workspace' ? 'contract' : collection,
    }, true);
  },
  getContractProductInfo: ({ docId, productId, ratePackage, collection }) => {
    return callApiJWT(`/contracting/getProductInfo/${docId}/${productId}/${ratePackage}`, 'post', {
      query: collection === 'contract' || collection === 'workspace' ? contractInfoQueries.getProductDetailsQuery : contractInfoQueries.draftTemplateProductQuery,
      contractId: docId,
      productId,
      collection: collection === 'workspace' ? 'contract' : collection,
    });
  },
  manageExtrasOffers: ({ draftId, list, type }) => {
    return callApiJWT(
      `/contracting/contract/product/shared-data`,
      'put',
      { draftId, list, type },
    );
  },
  archiveUnarchiveTemplate: ({ templateId, isArchived }) => callApi(`/proxy/contracting/template/${templateId}/archiveUnarchive`, 'put', { isArchived }),
  updateFolders: ({ folders, originalFolder, newFolderName }) => {
    return callApi('/proxy/contracting/contract-list/folder', 'put', { folders, originalFolder, newFolderName });
  },
  updatePartnerForSentDoc: ({ contractId, ...extraPayload }) =>
    callApi(`/proxy/contracting/contract/${contractId}/partner`, 'put', extraPayload),
  addFilesToContract: ({ contractId, files, msgId }) =>
    callApi(`/proxy/contracting/contract/files`, 'post', { contractId, files, msgId }),
  submitContractingComment: ({ historyFeedId, ...payload }) =>
    callApi(`/proxy/contracting/saveComment/${historyFeedId}`, 'put', payload),
  getContractForwardingEmail: (contractId) => callApi(`/proxy/contracting/contract/forwardingemail`, 'post', { contractId }),
  getDiffReport: ({ docs, onlyGetSummary }) => callApi(`/proxy/contracting/report/diff/${docs}?onlyGetSummary=${onlyGetSummary}`, 'get'),
  /*
  * difference between getTemplates and getTemplateList
  *
  * getTemplates only provides completed templates and template groups,
  * and is currently being used in send rates wizard
  *
  * getTemplateList provides all templates, initial drafts, and template groups
  * */
  getTemplates: ({ query }) => {
    const qs = query ? queryString.stringify({ additionalQuery: JSON.stringify(query) }) : '';
    return callApi(`/proxy/contracting/templates?${qs}`);
  },
  getTemplateList: () => callApi(`/proxy/contracting/template-list?`),
  getTemplatesForLocOrCC: ({ locationId, searchInput }) => {
    return callApi(`/proxy/contracting/location/${locationId}/templates?searchInput=${searchInput}`);
  },
  getContracts: ({ query, includeTemplateNames = false }) => {
    const qs = query ? queryString.stringify({ additionalQuery: JSON.stringify(query), includeTemplateNames }) : '';
    return callApi(`/proxy/contracting/contracts?${qs}`);
  },
  getFolders: () => callApi('/proxy/contracting/contract-list/folder'),
  sendContract: ({ payload }) => callApi('contracting/supplier/sendContracts', 'post', payload),
  sendContractRequest: ({ payload }) => callApi('contracting/request', 'post', payload),
  supplierSendUpdates: ({ templateIds, subject }) => callApi('contracting/supplier-updates', 'post', { templateIds, subject }),
  updateSameAsCorporatePolicyNotes: ({ templateDraftId, sameAsCorporatePolicyNotes }) =>
    callApi('/proxy/contracting/draft/sameAsCorporatePolicyNotes', 'put', { templateDraftId, sameAsCorporatePolicyNotes }),
  removePolicyNotesTemplate: templateDraftId =>
    callApi(`/proxy/contracting/draft/policyNotesTemplate/${templateDraftId}`, 'delete'),
  contractFromHF: payload => callApi(`contracting/contractIdFromHistoryFeedId/${payload.historyFeedId}`, 'post', payload),
  addDraftToQueue: ({
    templateId,
    source,
    sourceId,
    message,
  }) => callApi(`/proxy/contracting/supplier-updates/queue`, 'post', {
    templateId,
    source,
    sourceId,
    message,
  }),
  removeDraftOutOfQueue: ({
    templateId,
    sourceId,
  }) => callApi('/proxy/contracting/supplier-updates/queue', 'delete', {
    templateId,
    sourceId,
  }),
  removeSingleFeedByTCAdmin: ({
    messageId,
    historyFeedId,
  }) => callApi(`/proxy/asset-activity/${messageId}`, 'put', {
    assetId: historyFeedId,
  }),
  fileContractForRequest: ({
    requestId,
    contractId,
  }) => callApi('/proxy/contracting/buyer/fileContractForRequest', 'post', {
    requestId,
    contractId,
  }),
  removeContractsFromRequests: ({
    requestId,
    contractIds,
  }) => callApi('/proxy/contracting/buyer/removeContractsFromRequests', 'post', {
    requestId,
    contractIds,
  }),
  updateContractStatus: payload => callApi(
    payload.forAL ? '/landingproxy/contracting/updateContractStatus' : '/proxy/contracting/updateContractStatus', 'put', payload, true),
  requestDL: payload => callApiJWT('/contracting/requestDL', 'put', payload),
  getTemplateChangeLog: (threadId, search) => callApiJWT(
    `/contracting/template/logs/${threadId}${search}`,
    'get'
  ),
  createTemplatesInDummyAcct: payload => callApiJWT('/contracting/cr-dl/start', 'post', payload),
  acceptDummyTemplate: payload => callApiJWT('/contracting/dummy/template/accept', 'put', payload),
  sendRCMsg: payload => callApiJWT('/contracting/send-rc-msg', 'post', payload),
  // update data in the rate container/request that's only for the buyer
  updateWorkspaceContract: payload => callApiJWT('/contracting/workspace-contract', 'put', payload),
  submitRCRates: payload => callApiJWT(`/contracting/submitRCRates`, 'post', payload),
  createTemplateDraft: payload => callApiJWT(`/contracting/draft/${payload.contractId}`, 'post', payload),
  createEditedDraftForBuyer: payload => callApiJWT('/contracting/buyer/draft', 'post', payload),
  omniImport: payload => callApiJWT('/contracting/template/omniimport', 'put', payload),
  deleteTemplateDraft: payload => callApiJWT('/contracting/template/draft', 'delete', payload),
  loadDataForIframe: payload => {
    return callApiJWT(`/contracting/getContractInfo/${payload.rateDocId}`, 'post', {
      query: `
          query getContract ($contractId: ID!, $collection: String!, $viewerId: ID!, $viewBy: String!) {
            DraftnTemplate (id: $contractId, collection: $collection, viewerId: $viewerId, viewBy: $viewBy) {
              completionPercent
              acceptedBy
              crIds
              ownerIds
              defaultInvitees
              signatures {
                 invitee
                 inviteSentAt
                 signedBy
                 signedAt
              }
              originFrom
              draftType
              editHistory {
                detail
                productId
                ratePackageName
              }
            }
          }
        `,
      contractId: payload.rateDocId,
      collection: payload.collection,
    });
  },
  deleteEditedDraft: originalContractId => callApiJWT(`/contracting/discardEditedDraft/${originalContractId}`, 'delete'),
  updateProductNameBeddingConfig: payload => callApiJWT(`/contracting/updateProductNameBeddingConfig`, 'put', payload),
  makeACopyForBuyer: payload => callApiJWT('/contracting/buyer/makeACopy', 'post', payload),
  resendContract: payload => callApiJWT(`/contracting/resendContract/${payload.contractId}`, 'put', payload),
};

export const masterTemplates = {
  getAll: type => callApi(`/proxy/contracting/master-templates/${type}`, 'get'),
  getOne: id => callApi(`/proxy/contracting/master-template/${id}`, 'get'),
  getDiff: ({ lhsDocId, rhsDocId, onlyGetSummary }) => callApi(`/proxy/contracting/master-template-diff`, 'post', { lhsDocId, rhsDocId, onlyGetSummary }),
  createInitial: ({ type, templateName }) => callApi('/proxy/contracting/initial-master-template-draft', 'post', { type, templateName }),
  createEditedDraft: id => callApi('/proxy/contracting/edited-master-template-draft', 'post', { id }),
  edit: (id, updates) => callApi(`/proxy/contracting/master-template/edit/${id}`, 'put', { updates }),
  finalise: draftId => callApi('/proxy/contracting/master-template/finalise', 'put', { draftId }),
  applyMasterToTemplates: ({
    masterTemplateId,
    type,
    templateDraftIds,
    sameAsCorporatePolicyNotes,
    honorPreloadContent,
  }) => callApi('/proxy/contracting/master-template/associate/templates', 'put', {
    masterTemplateId,
    type,
    templateDraftIds,
    sameAsCorporatePolicyNotes,
    honorPreloadContent,
  }),
  discardDraft: draftId => callApi(`/proxy/contracting/master-template-draft/${draftId}`, 'delete'),
  getAssociatedTemplates: ({ id, type }) => callApi('/proxy/contracting/master-template/templates', 'post', { id, type }),
  renameTemplate: ({ id, newName }) => callApi('/proxy/contracting/master-template/name', 'put', { id, newName }),
  deleteTemplate: ({ id }) => callApi(`/proxy/contracting/master-template/${id}`, 'delete'),
  getAffectedBuyers: id => callApi(`/proxy/contracting/master-template/buyers/${id}`, 'get'),
};

export const admin = {
  toogleTourWriter: (companyId, enabled) => callApi('/proxy/admin/toggleTourWriter', 'post', { companyId, enabled }),
  toogleTourplan: (companyId, enabled) => callApi('/proxy/admin/toggleTourplan', 'post', { companyId, enabled }),
  convertUserToAssociatedEmail: userId => callApi(`/proxy/admin/turnUserIntoAssociatedEmail`, 'post', { userId }),
  consolidateAccount: (email, companyId, associateEmail) => callApi(`/proxy/admin/consolidate`, 'post', { email, companyId, associateEmail, getList: false }),
  consolidateAccountTest: (email, companyId) => callApi(`/proxy/admin/consolidate`, 'post', { email, companyId, getList: true }),
  updatePropertyNameForContractingDocs: (payload) => callApi('/proxy/admin/contracting/docs', 'put', payload),
  getSignedToken: payload => callApi('/proxy/admin/getSignedToken', 'post', payload),
  getSessionInfo: () => callApi('/getSessionInfo', 'get', null, true),
  getDLQueue: () => callApi('/proxy/dl-queue', 'get'),
  updateTemplateInQueue: payload => callApi(
    '/proxy/dl-queue/template',
    'put',
    payload,
  ),
  addNote: payload => callApi('/proxy/dl-queue/note', 'post', payload),
  addTemplate: id => callApi('/proxy/dl-queue/template', 'post', { id }),
  deleteTemplateFromQueue: templateId => callApi(`/proxy/dl-queue/template/${templateId}`, 'delete'),
  deleteSupplierFromQueue: supplierId => callApi(`/proxy/dl-queue/supplier/${supplierId}`, 'delete'),
  toggleTCVerified: payload => callApi('admin/toggleTCVerified', 'post', payload),
  resendBulkMessage: payload => callApi('admin/resendBulkMessage', 'post', payload),
};

export const connection = {
  pendingInvites: payload => callApi(`/proxy/connections?status=invites&get=${payload.get}`, 'get'),
};

export const massMessage = {
  newMessage: (body) => callApi('workspace/newMassMessage', 'post', body),
  getPartners: () => callApi('partners', 'get'),
};

export const profile = {
  getUserLocationProfile: ({ locationId } = {}) => {
    const qs = queryString.stringify({ query: myLocationProfileQuery });
    return callApi(`/proxy/profile/locations/${locationId}?${qs}`, 'get');
  },
  getCompanyProfile: () => callApi(`/proxy/profile?query=${profileQuery}`, 'get'),
  updateGroup: payload => callApi('/proxy/profile/group', 'put', payload),
  deleteGroup: payload => callApi('/proxy/profile/group', 'delete', payload),
  reorderLocations: locationIds => callApi('/proxy/profile/reorderLocations', 'put', { locationIds }),
  reorderProducts: productIds => callApi('/proxy/profile/reorderProducts', 'put', { productIds }),
  editProductField: ({ updateField, updateBody, productId, locationId }) => {
    let body = updateBody;
    if ((updateField === 'coverImageUrl' || updateField === 'profileImageUrl') && updateBody) {
      body += `?${Date.now()}`;
    }
    return callApiJWT(`/profile/products/${productId}?mutation=${myProductMutation}`, 'put', {
      locationId,
      update: { [updateField]: body },
    });
  },
};

export const assetLanding = {
  ezLoadRequest: payload => callApiwithToken('/dl-queue/supplier', 'post', {
    supplierId: payload.supplierId,
    files: payload.files,
    requests: payload.requests,
    email: payload.email,
  }),
  sendExternalRates: payload => callApiwithToken('/landing/sendExternalRates', 'post', {
    subject: payload.subject,
    message: payload.message,
    files: payload.files,
    requests: payload.requests,
    userId: payload.userId,
    fromEmail: payload.fromEmail,
    supplierId: payload.supplierId,
  }),
  authByToken: (hashToken = 'oldUrl', encryptedEmail) => callApi('/authByToken', 'post', { hashToken, encryptedEmail }, true),
  verifyToken: (hashToken = 'oldUrl', oldUrlPayload) => callApiwithToken(`/verifyToken/${hashToken}`, 'post', { oldUrlPayload }),
  getAssets: senderId => callApiwithToken(`/landing/assets?senderId=${senderId}`, 'get', null),
  // TODO: replace with real endpoint
  checkDummyAccount: payload => callApiwithToken(
    `/landing/checkDummyAccount?hashToken=${payload.shortid}&contractId=${payload.contractId}`,
    'get'
  ),
  signRateDocs: payload => callApiwithToken(`/contracting/submitRCRates`, 'post', payload),
  markDummyTemplateAsViewed: payload => callApiwithToken('/contracting/dummy/template/viewed', 'put', payload),
};

export const settings = {
  getSettings: () => callApi('settings', 'get'),
  saveSettings: payload => callApi('settings', 'put', payload),
};

// Contentful for sidebar

const buildURLQuery = obj =>
  Object.entries(obj)
        .map(pair => pair.map(encodeURIComponent).join('='))
        .join('&');

function contentfulApi({
  accessToken = `${process.env.CONTENTFUL_HELPSIDEBAR}`,
  bodyParam = null,
  endpoint,
  environment = 'master',
  method = 'get',
  searchParams = {},
  space = 'wwevjgfp8hg4',
} = {}) {
  searchParams.access_token = accessToken;
  const fullUrl = `${API_CONTENTFUL}spaces/${space}/environments/${environment}/${endpoint}?${buildURLQuery(searchParams)}`;
  const body = bodyParam ? JSON.stringify(bodyParam) : null;
  return fetch(fullUrl, {
    method,
    headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
    body,
  })
  .then(response =>
    response.json().then(json => ({ json, response }))
  ).then(({ json, response }) => {
    if (!response.ok) {
      return Promise.reject(json);
    }
    if (Array.isArray(json)) return json;
    return Object.assign({}, json);
  })
  .then(
    response => ({ response }),
    error => ({ error: error || 'Something bad happened' })
  );
}

export const contentful = {
  sidebarHelp: {
    getAllEntries: () => contentfulApi({ endpoint: 'entries', searchParams: { content_type: 'sidebarHelp', select: 'sys.id,fields.matchingUrls,fields.matchingRole,fields.title' } }),
    getHelp: (articleId) => contentfulApi({ endpoint: `entries`, searchParams: { content_type: 'sidebarHelp', 'sys.id': articleId } }),
  },
};

export const integration = {
  saveExportResponse: payload => callApiJWT('/integration/saveExportResponse', 'post', payload),
  getJobStatus: payload => callApi(`/integrationproxy/jobStatus?jobId=${payload.jobId}`, 'get', null, true),
};

export const billing = {
  getInvoices: payload => callApiJWT(`/billing/invoices?starting_after=${payload.starting_after || ''}&ending_before=${payload.ending_before || ''}`, 'get'),
};



// WEBPACK FOOTER //
// ./src/app/api.js